// Explore — real category browsing page, backed by /browse/categories.
// Previously "/explore" just unconditionally redirected to "/", dropping the
// ?category= param entirely -- every category tile on the landing page's "more
// categories" carousel was a dead link. This replaces that redirect with a page
// that actually filters and lists live streams for the requested category.
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Tv, ArrowLeft } from "lucide-react";
import api from "../lib/api";

export default function Explore() {
  const [params] = useSearchParams();
  const categoryId = params.get("category") || "";
  const [cats, setCats] = useState(null);

  useEffect(() => {
    api.get("/browse/categories").then(({ data }) => setCats(data)).catch(() => setCats([]));
  }, []);

  if (cats === null) {
    return <div className="p-20 text-center text-white/40" data-testid="explore-loading">Loading categories…</div>;
  }

  const current = categoryId ? cats.find((c) => c.id === categoryId) : null;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="explore-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-white/50 hover:text-white transition">
        <ArrowLeft className="w-3.5 h-3.5" /> Back home
      </Link>

      {current ? (
        <>
          <div>
            <div className="label-eyebrow text-nexus-cyan mb-1">// category</div>
            <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">{current.label}</h1>
            <p className="text-white/50 mt-2 text-sm">{current.live_count} live right now</p>
          </div>
          {current.streams.length === 0 ? (
            <div className="border border-white/10 bg-nexus-surface/40 p-10 text-center text-white/40" data-testid="explore-empty">
              Nobody's live in {current.label} right now — check back soon.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="explore-stream-grid">
              {current.streams.map((s) => (
                <Link key={s.id} to={`/watch/${s.id}`} className="group block" data-testid={`explore-stream-${s.id}`}>
                  <div className="relative aspect-video overflow-hidden border border-white/10 group-hover:border-nexus-cyan transition">
                    <img src={s.thumbnail} alt="" className="w-full h-full object-cover" />
                    <div className="absolute top-1.5 left-1.5 bg-nexus-crimson px-1.5 py-0.5 text-[8px] tracking-[0.3em] font-display font-black uppercase flex items-center gap-1">
                      <Tv className="w-2.5 h-2.5" /> Live
                    </div>
                    <div className="absolute bottom-1.5 right-1.5 bg-black/70 px-1.5 py-0.5 text-[9px] text-white">{(s.viewers || 0).toLocaleString()} viewers</div>
                  </div>
                  <div className="mt-1.5 text-sm font-medium truncate">{s.title}</div>
                  <div className="text-[11px] text-white/50 truncate">{s.creator_name}</div>
                </Link>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div>
            <div className="label-eyebrow text-nexus-cyan mb-1">// browse</div>
            <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">All categories</h1>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="explore-category-grid">
            {cats.map((c) => (
              <Link key={c.id} to={`/explore?category=${c.id}`} className="border border-white/10 hover:border-nexus-cyan transition p-4" data-testid={`explore-cat-${c.id}`}>
                <div className="font-display font-bold">{c.label}</div>
                <div className="text-[11px] text-white/50 mt-1">{c.live_count} live</div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
